import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { useApi } from "@/hooks/useApi";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import useAccess from "@/hooks/useAccess";
import { useTranslation } from "@/hooks/useTranslation";
import { buildHubSpotOAuthUrl } from "@/lib/integrations";
import { AlertTriangleIcon, ExternalLinkIcon } from "lucide-react";
import { useState } from "react";
import { useToastError } from "./ui/use-toast";

type HubspotReconnectAlertProps = {
  translationPrefix: string;
  onReconnected?: () => void;
};

/**
 * Alert shown when HubSpot scopes are missing and a reconnect is needed.
 * Handles the OAuth reauthorization popup flow.
 */
export function HubspotReconnectAlert({
  translationPrefix,
  onReconnected,
}: HubspotReconnectAlertProps) {
  const { refetch: refetchHubSpotIntegration } = useHubSpotIntegration();
  const api = useApi();
  const [isReauthorizing, setIsReauthorizing] = useState(false);
  const toastError = useToastError();
  const { t } = useTranslation();
  const { hasAccess } = useAccess();
  const isManager = hasAccess({
    requiredRoles: [TeamUserRolesEnum.MANAGER, TeamUserRolesEnum.ADMIN],
  });

  const handleReauthorize = async () => {
    // Fetching the state puts a network round trip between the click and the
    // popup; without this guard a second click opens a second popup, and both
    // message listeners would then fight over which window to close.
    if (isReauthorizing) return;

    // Opened blank before any await: browsers only allow a popup during the
    // click itself, and Safari withdraws that permission once a network
    // response has landed — `window.open` then returns null.
    const oauthWindow = window.open("about:blank", "_blank");
    if (!oauthWindow) {
      toastError();
      return;
    }

    // Issued server-side and echoed back on the callback: without it the API
    // cannot tell this team's authorization from a link crafted by someone else.
    let state: string;
    try {
      setIsReauthorizing(true);
      const { data } = await api.post<{ state: string }>(
        "/integration/crm/hubspot/oauth-state"
      );
      state = data.state;
    } catch {
      oauthWindow.close();
      toastError();
      return;
    } finally {
      setIsReauthorizing(false);
    }

    const hubspotOAuthUrl = buildHubSpotOAuthUrl(
      window.location.origin + "/integrations/hubspot",
      state
    );
    if (!hubspotOAuthUrl) {
      oauthWindow.close();
      toastError();
      return;
    }

    oauthWindow.location.href = hubspotOAuthUrl;

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(popupCheckInterval);
    };

    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === "HUBSPOT_OAUTH_SUCCESS") {
        oauthWindow?.close();
        cleanup();
        await refetchHubSpotIntegration();
        onReconnected?.();
      } else if (event.data?.type === "HUBSPOT_OAUTH_ERROR") {
        oauthWindow?.close();
        cleanup();
        toastError();
      }
    };

    window.addEventListener("message", handleMessage);

    const popupCheckInterval = setInterval(() => {
      if (oauthWindow?.closed) {
        cleanup();
      }
    }, 500);
  };

  return (
    <Alert variant="warning">
      <AlertTriangleIcon className="h-4 w-4" />
      <AlertTitle>{t(`${translationPrefix}.reconnectTitle`)}</AlertTitle>
      <AlertDescription className="space-y-3">
        {isManager ? (
          <>
            <p>{t(`${translationPrefix}.reconnectDescription`)}</p>
            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleReauthorize}
              disabled={isReauthorizing}
            >
              <ExternalLinkIcon className="h-3.5 w-3.5" />
              {t(`${translationPrefix}.reconnectLink`)}
            </Button>
          </>
        ) : (
          <p>{t(`${translationPrefix}.reconnectContactManager`)}</p>
        )}
      </AlertDescription>
    </Alert>
  );
}
